import { useQuery, useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { NotificationListResponse, UnreadCountResponse } from '../types/notification';

export const notificationKeys = {
  all: ['notifications'] as const,
  list: (reunionId: string) => ['notifications', 'list', reunionId] as const,
  unreadCount: (reunionId: string) => ['notifications', 'unread-count', reunionId] as const,
};

export function useNotifications(reunionId: string) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(reunionId),
    queryFn: ({ pageParam }) =>
      api.get<NotificationListResponse>('/notifications', {
        reunionId,
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: '20',
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) =>
      lastPage.pagination.hasMore ? lastPage.pagination.nextCursor : undefined,
    enabled: !!reunionId,
  });
}

export function useUnreadCount(reunionId: string) {
  return useQuery({
    queryKey: notificationKeys.unreadCount(reunionId),
    queryFn: () =>
      api.get<UnreadCountResponse>('/notifications/unread-count', { reunionId }),
    refetchInterval: 60_000,
    enabled: !!reunionId,
  });
}

export function useMarkRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (notificationId: string) =>
      api.put(`/notifications/${notificationId}/read`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}

export function useMarkAllRead(reunionId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.put('/notifications/read-all', { reunionId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.all });
    },
  });
}
